import { useEffect, useRef } from 'react';

const NetworkCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationId;
    let time = 0;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.scale(dpr, dpr);
    };

    resize();
    window.addEventListener('resize', resize);

    // Grid of nodes
    const cols = 14;
    const rows = 18;
    const nodes = [];

    const initNodes = () => {
      nodes.length = 0;
      const rect = canvas.getBoundingClientRect();
      const spacingX = rect.width / (cols + 1);
      const spacingY = rect.height / (rows + 1);

      for (let r = 1; r <= rows; r++) {
        for (let c = 1; c <= cols; c++) {
          nodes.push({
            baseX: c * spacingX,
            baseY: r * spacingY,
            x: c * spacingX,
            y: r * spacingY,
            phase: Math.random() * Math.PI * 2,
            speed: 0.3 + Math.random() * 0.4,
            radius: 1 + Math.random() * 1.5,
            pulsePhase: Math.random() * Math.PI * 2,
          });
        }
      }
    };

    initNodes();
    window.addEventListener('resize', initNodes);

    const draw = () => {
      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, rect.width, rect.height);
      time += 0.008;

      // Update node positions with gentle drift
      for (const node of nodes) {
        node.x = node.baseX + Math.sin(time * node.speed + node.phase) * 8;
        node.y = node.baseY + Math.cos(time * node.speed * 0.7 + node.phase) * 6;
      }

      // Draw connections
      const connectionDist = 80;
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connectionDist) {
            const alpha = (1 - dist / connectionDist) * 0.12;
            // Pulse traveling along connections
            const pulse = Math.sin(time * 2 + i * 0.1) * 0.5 + 0.5;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.strokeStyle = `rgba(161, 161, 170, ${alpha * (0.5 + pulse * 0.5)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }

      // Draw nodes
      for (const node of nodes) {
        const pulse = Math.sin(time * 1.5 + node.pulsePhase) * 0.5 + 0.5;
        const r = node.radius * (0.8 + pulse * 0.4);
        const alpha = 0.15 + pulse * 0.25;

        // Glow
        ctx.beginPath();
        ctx.arc(node.x, node.y, r * 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(161, 161, 170, ${alpha * 0.15})`;
        ctx.fill();

        // Core dot
        ctx.beginPath();
        ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(161, 161, 170, ${alpha})`;
        ctx.fill();
      }

      // Occasional bright pulse traveling through the network
      const pulseNode = Math.floor((time * 3) % nodes.length);
      const pn = nodes[pulseNode];
      if (pn) {
        ctx.beginPath();
        ctx.arc(pn.x, pn.y, 3, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(228, 228, 231, 0.6)';
        ctx.fill();

        ctx.beginPath();
        ctx.arc(pn.x, pn.y, 8, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(228, 228, 231, 0.08)';
        ctx.fill();
      }

      animationId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('resize', initNodes);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full"
      style={{ opacity: 0.6 }}
    />
  );
};

export default NetworkCanvas;
